import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import { useToast } from '../contexts/ToastContext';
import {
  adminGetShopRequestQueue,
  adminGetDirectoryShops,
  adminShopModerationAction,
} from '../services/api/adminShopModeration';
import type { ShopRequest } from '../types/buildExpansion';
import type { AdminModerationShopRow } from '../services/api/adminShopModeration';
import { ShopStatusBadge } from './shops/ShopStatusBadge';
import { AdminQueueState } from './ui/AdminQueueState';
import { AdminDeleteButton } from './ui/AdminDeleteButton';

type TabKey = 'queue' | 'directory';

const STATUS_FILTERS = ['', 'pending', 'needs_info', 'approved', 'rejected'];

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

export const AdminShopModeration: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab] = useState<TabKey>('queue');
  const [statusFilter, setStatusFilter] = useState('');
  const [requests, setRequests] = useState<ShopRequest[]>([]);
  const [shops, setShops] = useState<AdminModerationShopRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ShopRequest | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState<AdminModerationShopRow | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (tab === 'queue') {
        setRequests(await adminGetShopRequestQueue(statusFilter || undefined));
      } else {
        setShops(await adminGetDirectoryShops(statusFilter || undefined));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load';
      setLoadError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (req: ShopRequest) => {
    setBusyId(req.id);
    try {
      await adminShopModerationAction({
        action: 'approve_request',
        request_id: req.id,
      });
      showToast('Shop approved and published.', 'success');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Approve failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.id);
    try {
      await adminShopModerationAction({
        action: 'reject_request',
        request_id: rejectTarget.id,
        notes: rejectNotes,
      });
      showToast('Request rejected.', 'success');
      setRejectTarget(null);
      setRejectNotes('');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Reject failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const setDirectoryStatus = async (shop: AdminModerationShopRow, status: string) => {
    setBusyId(shop.id);
    try {
      await adminShopModerationAction({
        action: 'directory_status',
        shop_id: shop.id,
        status,
      });
      showToast(`Shop marked ${status}.`, 'success');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Update failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const setVerification = async (shop: AdminModerationShopRow, verification_status: string) => {
    setBusyId(shop.id);
    try {
      await adminShopModerationAction({
        action: 'edit_shop',
        shop_id: shop.id,
        verification_status,
      });
      showToast('Verification updated.', 'success');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Update failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const runMerge = async () => {
    if (!mergeSource || !mergeTargetId) return;
    setBusyId(mergeSource.id);
    try {
      await adminShopModerationAction({
        action: 'merge',
        source_shop_id: mergeSource.id,
        target_shop_id: Number(mergeTargetId),
      });
      showToast('Shops merged.', 'success');
      setMergeOpen(false);
      setMergeSource(null);
      setMergeTargetId('');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Merge failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Shop moderation"
        description="Review shop requests, manage directory status, and merge duplicates."
        actions={
          <Button type="button" variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2 items-center">
        <div role="tablist" aria-label="Shop moderation views" className="flex gap-1">
          {(['queue', 'directory'] as TabKey[]).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => {
                setTab(key);
                setStatusFilter('');
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium min-h-[44px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
                tab === key ? 'bg-orange-500/15 text-orange-300 border border-orange-500/35' : 'text-gray-400 border border-transparent hover:bg-white/[0.04]'
              }`}
            >
              {key === 'queue' ? 'Request queue' : 'Directory'}
            </button>
          ))}
        </div>
        <label className="sr-only" htmlFor="shop-status-filter">
          Filter by status
        </label>
        <select
          id="shop-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input max-w-[200px] min-h-[44px]"
        >
          <option value="">All statuses</option>
          {STATUS_FILTERS.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-400" role="status">
          Loading…
        </p>
      ) : loadError ? (
        <AdminQueueState
          variant="error"
          title="Could not load shop moderation queue"
          description={loadError}
          onRetry={() => void load()}
        />
      ) : tab === 'queue' ? (
        requests.length === 0 ? (
          <AdminQueueState
            variant={statusFilter ? 'filter-empty' : 'empty'}
            title={statusFilter ? 'No requests match this filter' : 'Shop request queue is clear'}
            description={
              statusFilter
                ? 'Try a different status or clear the filter.'
                : 'New shop submissions from members will appear here.'
            }
            onClearFilter={statusFilter ? () => setStatusFilter('') : undefined}
          />
        ) : (
          <ul className="space-y-4 list-none">
            {requests.map((req) => (
              <li key={req.id} className="form-section">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-white">{req.name}</h3>
                    <p className="text-sm text-gray-400 mt-1">
                      Submitted by @{req.submitter_username ?? 'unknown'} · {formatDate(req.created_at)}
                    </p>
                    <p className="text-sm text-gray-300 mt-2">{req.why_add_reason}</p>
                    {req.description ? <p className="text-sm text-gray-400 mt-1">{req.description}</p> : null}
                    <p className="text-xs text-gray-500 mt-2">
                      {[req.city, req.state].filter(Boolean).join(', ')}
                      {req.website_url ? ` · ${req.website_url}` : ''}
                    </p>
                    {req.services.length > 0 && (
                      <ul className="flex flex-wrap gap-1 mt-2 list-none">
                        {req.services.map((s: string) => (
                          <li key={s} className="badge bg-[#141414] text-gray-300 border border-white/[0.08] text-xs">
                            {s}
                          </li>
                        ))}
                      </ul>
                    )}
                    <span className="inline-block mt-2 badge bg-amber-950/50 text-amber-300 border border-amber-800 text-xs">
                      {req.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {(req.status === 'pending' || req.status === 'needs_info') && (
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        loading={busyId === req.id}
                        onClick={() => void approve(req)}
                      >
                        Approve
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busyId === req.id}
                        onClick={() => {
                          setRejectTarget(req);
                          setRejectNotes('');
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                  {req.resulting_shop_id ? (
                    <Link to={`/shops/${req.resulting_shop_id}`} className="text-sm text-orange-300 hover:underline">
                      View published shop
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )
      ) : shops.length === 0 ? (
        <AdminQueueState
          variant={statusFilter ? 'filter-empty' : 'empty'}
          title={statusFilter ? 'No shops match this filter' : 'No shops in the directory yet'}
          description={
            statusFilter
              ? 'Try a different status or clear the filter.'
              : 'Approved shop requests will appear here after moderation.'
          }
          onClearFilter={statusFilter ? () => setStatusFilter('') : undefined}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="text-gray-400 border-b border-white/[0.06]">
                <th className="py-2 pr-4">Shop</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Builds</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shops.map((shop) => (
                <tr key={shop.id} className="border-b border-white/[0.06]">
                  <td className="py-3 pr-4">
                    <Link to={`/shops/${shop.slug}`} className="text-orange-300 hover:underline font-medium">
                      {shop.name}
                    </Link>
                    <p className="text-xs text-gray-500">@{shop.creator_username ?? '—'}</p>
                  </td>
                  <td className="py-3 pr-4">
                    <ShopStatusBadge trustLabel={shop.trust_label ?? 'listed'} compact />
                    <p className="text-xs text-gray-500 mt-1">{shop.directory_status ?? 'approved'}</p>
                  </td>
                  <td className="py-3 pr-4 text-gray-300">{shop.build_count}</td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-1">
                      {shop.directory_status === 'approved' ? (
                        <Button type="button" variant="ghost" size="sm" disabled={busyId === shop.id} onClick={() => void setDirectoryStatus(shop, 'archived')}>
                          Archive
                        </Button>
                      ) : (
                        <Button type="button" variant="ghost" size="sm" disabled={busyId === shop.id} onClick={() => void setDirectoryStatus(shop, 'approved')}>
                          Restore
                        </Button>
                      )}
                      <Button type="button" variant="ghost" size="sm" disabled={busyId === shop.id} onClick={() => void setVerification(shop, 'verified')}>
                        Verify
                      </Button>
                      <Button type="button" variant="ghost" size="sm" disabled={busyId === shop.id} onClick={() => void setVerification(shop, 'community_verified')}>
                        Community
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busyId === shop.id}
                        onClick={() => {
                          setMergeSource(shop);
                          setMergeTargetId('');
                          setMergeOpen(true);
                        }}
                      >
                        Merge
                      </Button>
                      {(shop.directory_status === 'archived' || shop.directory_status === 'closed') && (
                        <AdminDeleteButton
                          entity="shop"
                          id={shop.id}
                          confirmToken={shop.slug}
                          label={shop.name}
                          description={
                            <>
                              Hard-deletes shop <strong className="text-white">{shop.name}</strong>, members,
                              requests, and contributor links. Vehicles and user accounts are preserved.
                            </>
                          }
                          disabled={busyId === shop.id}
                          onDeleted={() => void load()}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(rejectTarget)}
        title="Reject shop request?"
        description="The submitter will not be notified automatically. Add notes for your records."
        confirmLabel="Reject"
        cancelLabel="Cancel"
        loading={busyId !== null}
        onCancel={() => setRejectTarget(null)}
        onConfirm={() => void confirmReject()}
      >
        <textarea
          className="input input-textarea w-full mt-3"
          placeholder="Optional rejection notes"
          value={rejectNotes}
          onChange={(e) => setRejectNotes(e.target.value)}
          aria-label="Rejection notes"
        />
      </ConfirmDialog>

      <Modal isOpen={mergeOpen} onClose={() => setMergeOpen(false)} title="Merge duplicate shop" size="md">
        <p className="text-sm text-gray-300 mb-4">
          Merge <strong className="text-white">{mergeSource?.name}</strong> into another shop. Contributors will move to the target; the source will be archived.
        </p>
        <label htmlFor="merge-target" className="input-label">
          Target shop ID
        </label>
        <input
          id="merge-target"
          type="number"
          className="input mb-4"
          value={mergeTargetId}
          onChange={(e) => setMergeTargetId(e.target.value)}
          min={1}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => setMergeOpen(false)}>
            Cancel
          </Button>
          <Button type="button" variant="primary" loading={busyId !== null} onClick={() => void runMerge()}>
            Merge
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminShopModeration;
